"use client";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { Emotion, Papers } from "./catalog";
import { DwimEngine } from "./engine";
import { dwimAriaLabel, poseToPrimitives, SCENE_VIEWBOX } from "./geometry";
import { BUBBLE_FONT_FAMILY } from "./bubble";
import type { Preset } from "./presets";
import { resolveKnobs } from "./presets";
import type { CaptionOptions, DwimSize, DwimTheme } from "./theme";
import { pixelSize, themes } from "./theme";

export type DwimProps = {
  emotion?: Emotion;
  papers?: Papers;
  preset?: Preset;
  theme?: DwimTheme;
  size?: DwimSize;
  reducedMotion?: boolean;
  captionReducedMotion?: boolean;
};

export type DwimHandle = {
  speak: (text: string, options?: CaptionOptions) => Promise<void>;
  think: (text: string, options?: CaptionOptions) => Promise<void>;
  silence: () => void;
};

export const Dwim = forwardRef<DwimHandle, DwimProps>(function Dwim(
  {
    emotion = "neutral",
    papers = "none",
    preset,
    theme = themes.paper,
    size = "hero",
    reducedMotion = false,
    captionReducedMotion,
  },
  ref,
) {
  const engineRef = useRef(new DwimEngine());
  const tickedRef = useRef(false);
  const afterTickRef = useRef<Array<() => void>>([]);
  const [mounted, setMounted] = useState(false);
  const [pose, setPose] = useState(() => engineRef.current.pose());

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    engineRef.current.setInput({
      ...resolveKnobs(preset, emotion, papers),
      size,
      reducedMotion,
      captionReducedMotion,
    });
  }, [preset, emotion, papers, size, reducedMotion, captionReducedMotion]);

  useEffect(() => {
    const engine = engineRef.current;
    let frame = 0;
    const loop = () => {
      // rAF schedules only; tick uses Date.now() so idle blink/glance/peek fire.
      engine.tick();
      setPose(engine.pose());
      if (!tickedRef.current) {
        tickedRef.current = true;
        const queued = afterTickRef.current;
        afterTickRef.current = [];
        for (const run of queued) run();
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, []);

  useImperativeHandle(ref, () => {
    const engine = engineRef.current;
    const afterTick = <T,>(fn: () => T): Promise<T> => {
      if (tickedRef.current) return Promise.resolve(fn());
      return new Promise((resolve, reject) => {
        afterTickRef.current.push(() => {
          try {
            resolve(fn());
          } catch (err) {
            reject(err);
          }
        });
      });
    };
    return {
      speak: (text: string, options?: CaptionOptions) =>
        afterTick(() => engine.speak(text, options)).then((p) => p),
      think: (text: string, options?: CaptionOptions) =>
        afterTick(() => engine.think(text, options)).then((p) => p),
      silence: () => {
        void afterTick(() => {
          engine.silence();
        });
      },
    };
  }, []);

  const px = pixelSize(size);
  if (!mounted) {
    return (
      <svg
        width={px}
        height={px}
        viewBox={SCENE_VIEWBOX}
        overflow="visible"
        role="img"
        aria-label="The Dwim"
        style={{ background: theme.background, overflow: "visible" }}
      />
    );
  }

  return (
    <svg
      width={px}
      height={px}
      viewBox={SCENE_VIEWBOX}
      overflow="visible"
      role="img"
      aria-label={dwimAriaLabel(pose.bubble)}
      style={{ background: theme.background, overflow: "visible" }}
    >
      {poseToPrimitives(pose, theme, px).map((p, i) => {
        if (p.type === "ellipse")
          return (
            <ellipse
              key={i}
              cx={p.cx}
              cy={p.cy}
              rx={p.rx}
              ry={p.ry}
              fill={p.fill}
              stroke={p.stroke}
              strokeWidth={p.strokeWidth}
            />
          );
        if (p.type === "path")
          return (
            <path
              key={i}
              d={p.d}
              fill={p.fill}
              stroke={p.stroke}
              strokeWidth={p.strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        if (p.type === "rect")
          return (
            <rect
              key={i}
              x={p.x}
              y={p.y}
              width={p.width}
              height={p.height}
              fill={p.fill}
              stroke={p.stroke}
              strokeWidth={p.strokeWidth}
              transform={`rotate(${p.rotation} ${p.x + p.width / 2} ${p.y + p.height / 2})`}
            />
          );
        return (
          <text
            key={i}
            x={p.x}
            y={p.y}
            fontSize={p.fontSize}
            fill={p.fill}
            textAnchor={p.anchor}
            fontFamily={p.fontFamily ?? BUBBLE_FONT_FAMILY}
          >
            {p.text}
          </text>
        );
      })}
    </svg>
  );
});
